import React from "react";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import { setSelectedAccountsToLink } from "../features/account/accountSlice";
import { useNavigate } from "react-router-dom";
import Button from "../components/ui/Button";

const UserBankAccount: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { accounts, loading, error } = useAppSelector((s) => s.account);
    const { selectedFip } = useAppSelector((s) => s.fip);

    const [selectedAccounts, setSelectedAccounts] = React.useState<string[]>([]);

    // We no longer need local queue state here. 
    // It's handled after the consent screen.

    // Account discovery is now triggered from DiscoveryPrompt.tsx
    // This page only handles selection and linking.

    const toggleAccountSelection = (accountId: string) => {
        setSelectedAccounts(prev =>
            prev.includes(accountId)
                ? prev.filter(id => id !== accountId)
                : [...prev, accountId]
        );
    };

    const handleProceed = () => {
        if (selectedAccounts.length === 0 || !selectedFip) return;

        // Grab the full account objects based on the selected IDs
        const selectedBankAccounts = accounts.filter(a => selectedAccounts.includes(a.ACC_ID));

        // Save them to Redux so the Consent screen knows what the user plans to share/link
        dispatch(setSelectedAccountsToLink(selectedBankAccounts));

        // Move directly to Link accounts
        navigate("/linkOtpQueue");
    };

    return (
        <div className="min-h-screen bg-fin-bg p-6">
            <div className="max-w-2xl mx-auto">
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-white">Your Accounts</h1>
                    <p className="mt-1 text-fin-slate">Select accounts to link with your profile</p>
                </div>

                {selectedFip && (
                    <div className="mb-8 p-4 bg-fin-surface border border-fin-border rounded-xl flex items-center gap-4">
                        <div className="h-12 w-12 flex-shrink-0 p-1 border border-fin-border rounded-full bg-fin-bg">
                            {selectedFip.LOGO_URL ? (
                                <img src={selectedFip.LOGO_URL} alt={selectedFip.FIP_NAME} className="h-full w-full object-contain" />
                            ) : (
                                <div className="h-full w-full flex items-center justify-center text-xs font-bold text-fin-slate">
                                    {selectedFip.FIP_ID.substring(0, 2)}
                                </div>
                            )}
                        </div>
                        <div>
                            <p className="text-xs text-fin-slate uppercase tracking-wide font-semibold">Selected Bank</p>
                            <p className="font-bold text-white">{selectedFip.FIP_NAME}</p>
                        </div>
                    </div>
                )}

                {loading && (
                    <div className="flex flex-col items-center justify-center py-16">
                        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-fin-primary mb-4"></div>
                        <p className="text-fin-slate font-medium animate-pulse">Scanning for your accounts...</p>
                    </div>
                )}

                {error && (
                    <div className="bg-red-900/10 border border-red-900/20 text-red-400 p-4 rounded-lg mb-6 flex items-start">
                        <svg className="h-5 w-5 text-red-500 mr-3 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <div>
                            <h3 className="text-sm font-bold">Discovery Error</h3>
                            <p className="text-sm mt-1">{error}</p>
                        </div>
                    </div>
                )}

                <div className="space-y-4 mb-24">
                    {accounts.map((a) => {
                        const isSelected = selectedAccounts.includes(a.ACC_ID);
                        return (
                            <div
                                key={a.ACC_ID}
                                onClick={() => toggleAccountSelection(a.ACC_ID)}
                                className={`flex items-start p-4 bg-fin-surface rounded-xl border transition-all cursor-pointer ${isSelected
                                    ? "border-fin-primary bg-fin-primary/5 shadow-none"
                                    : "border-fin-border hover:border-fin-primary/30"
                                    }`}
                            >
                                <div className="flex-1">
                                    <p className="text-xs text-fin-primary font-bold uppercase tracking-wider mb-1">{a.ACC_TYPE}</p>
                                    <p className="font-mono text-lg font-bold text-white tracking-tight">{a.MASKED_ACC_NO}</p>
                                    <p className="text-xs text-fin-slate mt-1">Account ID: {a.ACC_ID}</p>
                                </div>
                                <div className="ml-4 mt-2">
                                    <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center transition-colors ${isSelected
                                        ? "border-fin-primary bg-fin-primary"
                                        : "border-fin-border bg-fin-bg"
                                        }`}>
                                        {isSelected && (
                                            <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {accounts.length === 0 && !loading && !error && (
                    <div className="text-center py-16 bg-fin-surface rounded-xl border border-dashed border-fin-border">
                        <div className="mx-auto h-12 w-12 bg-fin-bg rounded-full flex items-center justify-center mb-3">
                            <svg className="h-6 w-6 text-fin-slate" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                        </div>
                        <h3 className="text-white font-medium">No accounts found</h3>
                        <p className="text-fin-slate text-sm mt-1 max-w-xs mx-auto">We couldn't find any accounts linked to your mobile number at {selectedFip?.FIP_NAME}.</p>
                        <Button
                            variant="outline"
                            className="mt-6 border-fin-border text-fin-slate hover:bg-fin-bg"
                            onClick={() => navigate("/userBank")}
                        >
                            Try another bank
                        </Button>
                    </div>
                )}
            </div>

            {/* Sticky Footer for Multi-Select */}
            {accounts.length > 0 && !loading && !error && (
                <div className="fixed bottom-0 left-0 right-0 w-full bg-fin-surface border-t border-fin-border p-4 pb-6 z-40">
                    <div className="max-w-2xl mx-auto flex justify-between items-center">
                        <div className="text-left">
                            <p className="text-sm font-bold text-white">{selectedAccounts.length} Selected</p>
                            <p className="text-xs text-fin-slate">Accounts to link</p>
                        </div>
                        <Button
                            variant="primary"
                            onClick={handleProceed}
                            disabled={selectedAccounts.length === 0}
                            className="px-8 py-3"
                        >
                            Link & Proceed
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserBankAccount;
